import { Component, forwardRef, Input, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, first, map, tap } from 'rxjs/operators';
import { DataFieldGroupContainerComponent } from 'src/app/shared/datafields/datafield-group-container.component';
import { DataFieldGroup } from 'src/app/shared/datafields/models';
import { conditionalValidator } from 'src/app/shared/validators/conditional-validator';
import { Stakeholder } from 'src/app/stakeholder/models/stakeholder';
import { MonitorGroup } from '../../models/monitor-group';
import { MonitorItem, MonitorItemClass } from '../../models/monitor-item';
import { MonitorSchedule } from '../../models/monitor-schedule';
import { MonitorService } from '../../services/monitor.service';

import { DurationPickerComponent } from '../../../shared/duration-picker/duration-picker.component';
import { DeveloperArtifactAssemblyPickerComponent } from '../../../developer/developer-artifacts/developer-artifact-assembly-picker.component';
import { DataFieldGroupContainerComponent as DataFieldGroupContainerComponent_1 } from '../../../shared/datafields/datafield-group-container.component';

@Component({
    selector: 'app-monitor-item-edit',
    templateUrl: './monitor-item-edit.component.html',
    styleUrls: ['./monitor-item-edit.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MonitorItemEditComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => MonitorItemEditComponent),
            multi: true,
        }],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, DurationPickerComponent, DeveloperArtifactAssemblyPickerComponent, DataFieldGroupContainerComponent_1]
})
export class MonitorItemEditComponent implements OnDestroy, ControlValueAccessor, Validator {
  @ViewChild('dfgContainer', { static: false }) private dfgContainer: DataFieldGroupContainerComponent = null;

  @Input() stakeholders: Stakeholder[] = [];
  @Input() monitorGroups: MonitorGroup[] = [];
  @Input() monitorSchedules: MonitorSchedule[] = [];
  @Input() embeddedArtifactId: string = null;

  readonly suppressToStatuses = [
    { id: '', name: 'Do not suppress' },
    { id: 'Ok', name: 'Ok' },
    { id: 'Warning', name: 'Warning' }
  ];

  readonly formGroup: UntypedFormGroup;
  readonly artifactConfigValidationGroup = new UntypedFormGroup({});
  readonly subscriptions: Subscription[] = [];

  artifactDfgs: DataFieldGroup[] = [];

  constructor(
    fb: UntypedFormBuilder,
    private monitorService: MonitorService,
  ) {
    this.formGroup = fb.group({
      id: [0],
      name: ['', Validators.required],
      enabled: [true],
      groupId: [0, Validators.min(1)],
      stakeholderId: [0],
      stakeholderUrgent: [false],
      stakeholderContinuous: [false],
      scheduleId: [0],
      pollingInterval: [300, [Validators.required, Validators.min(30)]],
      description: [''],
      suppressTo: [''],
      suppressTimes: [3, [Validators.required, Validators.min(1)]],
      artifactId: ['', conditionalValidator(() => !this.isEmbeddedMonitorItem, Validators.required)],
      embeddedTypeArea: [''],
      embeddedTypeId: [0],
      artifactConfig: [''],
    });

    // Implement the Touched part of the ControlValueAccessor
    this.subscriptions.push(
      this.formGroup.statusChanges.pipe(
        first(() => this.formGroup.touched),
      ).subscribe(() => this.onTouched())
    );

    // Implement the Change part of the ControlValueAccessor
    this.subscriptions.push(
      this.formGroup.valueChanges
        .subscribe(e => this.onChange(new MonitorItemClass({
          id: Number(e.id),
          monitorGroupId: Number(e.groupId),
          stakeholderId: Number(e.stakeholderId),
          scheduleId: Number(e.scheduleId),
          enabled: e.enabled,
          name: e.name,
          description: e.description,
          stakeholderUrgent: Number(e.stakeholderId) ? e.stakeholderUrgent : false,
          stakeholderContinuous: Number(e.stakeholderId) ? e.stakeholderContinuous : false,
          artifactId: e.artifactId,
          embeddedTypeArea: e.embeddedTypeArea,
          embeddedTypeId: Number(e.embeddedTypeId),
          artifactConfig: e.artifactConfig,
          pollingInterval: e.pollingInterval,
          suppressToStatus: e.suppressTo,
          suppressMessagesCount: !!e.suppressTo ? Number(e.suppressTimes) : 0,
        })))
    );

    // Whenever the form status (disabled) is changed, manually reflect the status change to the
    // data field container via edit(commit as they are not forms controls
    this.subscriptions.push(
      this.formGroup.statusChanges.pipe(
        map(status => ({ status, disabled: this.formGroup.disabled })),
        tap(e => this.log('formGroup status changed to', e.status)),
        distinctUntilChanged((a, b) => a.disabled === b.disabled),
        tap(e => this.log('formGroup disabled changed to', e.disabled)),
      ).subscribe(e => {
        if (e.disabled) {
          this.dfgContainer?.commit();
        }
        else {
          this.dfgContainer?.edit();
        }
      }));

    // Whenever the value of 'Suppress Errors To' changes, set the enabled/disabled state of the
    // 'Suppress Number of Times' as required.
    this.subscriptions.push(
      this.formGroup.get('suppressTo').valueChanges.subscribe(value => {
        this.log('suppressTo value changed to', value);
        const fc = this.formGroup.get('suppressTimes') as UntypedFormControl;
        if (value) {
          if (!this.formGroup.disabled) {
            this.log('calling suppressTimes.enable()');
            fc.enable({ onlySelf: true });
          }
        }
        else {
          this.log('calling suppressTimes.disable()');
          fc.disable({ onlySelf: true }); // To prevent validation errors
        }
      }));

    // Whenever the state of 'Suppress Number of Times' is changed to anything but disabled,
    // check if that should be the case and disable again if appropriate. This occurs f.x. when
    // the form group is enabled but the value of 'Suppress Errors To' is 'Do not Suppress'.
    this.subscriptions.push(
      this.formGroup.get('suppressTimes').statusChanges.subscribe(e => {
        this.log('suppressTimes status changed to', e);

        if ((e !== 'DISABLED') && !this.formGroup.get('suppressTo').value) {
          this.formGroup.get('suppressTimes').disable({ onlySelf: true });
        }
      }));

    // Whenever the state of the artifact config validation group changes, reflect the change
    // through the ControlValueAccessor
    this.subscriptions.push(
      this.artifactConfigValidationGroup.statusChanges.subscribe(e => {
        // Reflect validation changes from the artifact validation group on the form group
        this.onValidatorChange();
      }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(e => e.unsubscribe());
    this.subscriptions.length = 0;
  }

  // Begin implement ControlValueAccessor

  writeValue(monitorItem: MonitorItem | null): void {
    this.formGroup.setValue({
      id: monitorItem?.id ?? 0,
      name: monitorItem?.name ?? '',
      enabled: monitorItem?.enabled ?? false,
      groupId: monitorItem?.monitorGroupId ?? 0,
      stakeholderId: monitorItem?.stakeholderId ?? 0,
      stakeholderUrgent: monitorItem?.stakeholderUrgent ?? false,
      stakeholderContinuous: monitorItem?.stakeholderContinuous ?? false,
      scheduleId: monitorItem?.scheduleId ?? '0',
      pollingInterval: monitorItem?.pollingInterval ?? 300,
      description: monitorItem?.description ?? '',
      suppressTo: monitorItem?.suppressToStatus ?? '',
      suppressTimes: monitorItem?.suppressMessagesCount ?? '',
      artifactId: monitorItem?.artifactId ?? '',
      artifactConfig: monitorItem?.artifactConfig ?? '',
      embeddedTypeArea: monitorItem?.embeddedTypeArea ?? '',
      embeddedTypeId: monitorItem?.embeddedTypeId ?? 0,
    });

    if (monitorItem) {
      this.refreshMonitorItemDataFieldGroups(monitorItem.artifactId);
    }
    else {
      this.artifactDfgs = [];
    }
  }

  private onChange = (value: MonitorItem | null) => undefined;
  registerOnChange(fn: (value: MonitorItem | null) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: any): void {
      this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.log('calling this.formGroup.disable()');
      this.formGroup.disable();
      this.log('this.formGroupDisabled returns', this.formGroupDisabled);
    }
    else {
      this.log('calling this.formGroup.enable()');
      this.formGroup.enable();
    }
  }

  // End implement ControlValueAccessor

  // Begin implementation Validator

  validate(control: AbstractControl): ValidationErrors {
    const errors1 = this.errorsFromFormGroup(this.formGroup);
    const errors2 = this.errorsFromFormGroup(this.artifactConfigValidationGroup);
    return errors1 ?? errors2;
  }

  private onValidatorChange = () => undefined;
  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  // End implementation Validator

  errorsFromFormGroup(formGroup: UntypedFormGroup): ValidationErrors {
    let errors: ValidationErrors = null;

    if (formGroup.invalid) {
      const controlKeys = Object.keys(formGroup.controls);
      const controls = controlKeys.map(e => formGroup.controls[e]);
      errors = controls.filter(e => e.errors).flatMap(e => e.errors);
    }

    return errors;
  }

  showError(path: string, errorCode?: string): boolean {
    const fc = this.formGroup.get(path) as UntypedFormControl;
    return errorCode ? fc.hasError(errorCode) : fc.invalid;
  }

  get formGroupDisabled(): boolean {
    const res = this.formGroup.disabled;
    this.log('get formGroupDisabled() returns', res, this.formGroup.status);
    return res;
  }

  get isStakeholderSelected(): boolean {
    const fc = this.formGroup.get('stakeholderId') as UntypedFormControl;
    return !!Number(fc.value);
  }

  get isSuppressTimesVisible(): boolean {
    const fc = this.formGroup.get('suppressTo') as UntypedFormControl;
    return !!fc.value;
  }

  get pollingIntervalIsInvalid(): boolean {
    const fc = this.formGroup.get('pollingInterval') as UntypedFormControl;
    return fc.invalid;
  }

  get selectedArtifactId(): string {
    const fc = this.formGroup.get('artifactId') as UntypedFormControl;
    return fc.value;
    }

    set selectedArtifactId(artifactId: string) {
        this.log(`set selectedArtifactId("${artifactId}") called`);
        const artifactIdFormControl = this.formGroup.get('artifactId') as UntypedFormControl;
        if (artifactIdFormControl.value !== artifactId) {
            this.refreshMonitorItemDataFieldGroups(artifactId);

            artifactIdFormControl.setValue(artifactId);
        }
    }


  refreshMonitorItemDataFieldGroups(artifactId?: string): void {
    this.artifactDfgs = [];
    const actualArtifactId = this.embeddedArtifactId ?? artifactId;

    this.log(`refreshMonitorItemDataFieldGroups("${actualArtifactId}") called, formGroup status is`, this.formGroup.status);
    if (actualArtifactId) {
      const artifactConfigFormControl = this.formGroup.get('artifactConfig') as UntypedFormControl;
      this.monitorService.getMonitorItemDataFieldGroups(actualArtifactId, artifactConfigFormControl.value)
        .then(result => {
          this.artifactDfgs = result;
          if (this.formGroup.status !== 'DISABLED') {
            // Give the new data fields time to render, then call edit
            setTimeout(() => {
              this.log(`refreshMonitorItemDataFieldGroups("${actualArtifactId}") calling container.edit(), formGroup status is`, this.formGroup.status);
              this.dfgContainer?.edit();
            });
          }
        })
        .catch(error => { console.error(error); });
    }
  }

  get isEmbeddedMonitorItem(): boolean {
    if (this.formGroup) {
      const fcArea = this.formGroup.get('embeddedTypeArea');
      if (fcArea.value && ('None'.localeCompare(fcArea.value) !== 0)) {
        return true;
      }
    }
    return false;
  }

  get artifactConfigDataFieldGroups(): DataFieldGroup[] {
    return this.dfgContainer?.datafieldGroups || [];
  }

  log(state: string, ...optionalParams: any[]) {
    // console.log(`MIE: ${state}`, ...optionalParams);
  }
}
